import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
    collection,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useUserAuth } from '../../../context/userAuthContext';
import {
    Plus, Search, Filter, Calendar, DollarSign, Eye, EyeOff, Edit3, Trash2,
    AlertCircle, CheckCircle, X, ArrowUpDown, Building2, Loader2, ChevronLeft,
    Bell, Globe, Settings, BarChart3, List, Grid
} from 'lucide-react';

const XFinanceSubscriptionsList = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();

    const [subscriptions, setSubscriptions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('nextPayment');
    const [viewMode, setViewMode] = useState('grid');
    const [loading, setLoading] = useState(false);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Define vibrant colors for the pie chart
    const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (user) {
            loadSubscriptions();
            loadCategories();
        }
    }, [user]);

    const loadSubscriptions = async () => {
        if (!user) return;

        setLoadingSubscriptions(true);
        try {
            const subscriptionsRef = collection(db, 'subscriptions', user.uid, 'userSubscriptions');
            const q = query(subscriptionsRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            const subscriptionsData = [];
            querySnapshot.forEach((doc) => {
                subscriptionsData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setSubscriptions(subscriptionsData);
        } catch (error) {
            console.error('Error al cargar suscripciones:', error);
            setMessage({ text: 'Error al cargar las suscripciones', type: 'error' });
        } finally {
            setLoadingSubscriptions(false);
        }
    };

    const loadCategories = async () => {
        if (!user) return;

        try {
            const categoriesRef = collection(db, 'subscriptions', user.uid, 'subscriptionCategories');
            const q = query(categoriesRef, orderBy('nombre', 'asc'));
            const querySnapshot = await getDocs(q);

            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setCategories(categoriesData);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    const toggleSubscriptionStatus = async (subscription) => {
        if (!user) return;

        setLoading(true);
        try {
            const subscriptionRef = doc(db, 'subscriptions', user.uid, 'userSubscriptions', subscription.id);
            await updateDoc(subscriptionRef, {
                activa: !subscription.activa,
                updatedAt: new Date(),
            });
            setMessage({
                text: `Suscripción ${!subscription.activa ? 'activada' : 'desactivada'} correctamente`,
                type: 'success'
            });
            loadSubscriptions();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            setMessage({ text: 'Error al cambiar el estado', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (subscriptionId, subscriptionName) => {
        if (!user || !window.confirm(`¿Está seguro de eliminar la suscripción "${subscriptionName}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const subscriptionRef = doc(db, 'subscriptions', user.uid, 'userSubscriptions', subscriptionId);
            await deleteDoc(subscriptionRef);
            setMessage({ text: 'Suscripción eliminada correctamente', type: 'success' });
            loadSubscriptions();
        } catch (error) {
            console.error('Error al eliminar suscripción:', error);
            setMessage({ text: 'Error al eliminar la suscripción', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredSubscriptions = subscriptions.filter(sub => {
        const matchesSearch = sub.nombre.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || sub.categoria === filterCategory;
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && sub.activa) ||
            (filterStatus === 'inactive' && !sub.activa);

        return matchesSearch && matchesCategory && matchesStatus;
    });

    const sortedSubscriptions = [...filteredSubscriptions].sort((a, b) => {
        switch (sortBy) {
            case 'nombre':
                return a.nombre.localeCompare(b.nombre);
            case 'cantidad':
                return b.cantidad - a.cantidad;
            case 'nextPayment':
                return new Date(a.fechaProximoPago) - new Date(b.fechaProximoPago);
            case 'categoria':
                return a.categoria.localeCompare(b.categoria);
            default:
                return 0;
        }
    });

    const getTotalMonthlyAmount = () => {
        return subscriptions
            .filter(sub => sub.activa)
            .reduce((total, sub) => {
                let monthlyAmount = sub.cantidad;
                switch (sub.frecuenciaPago) {
                    case 'semanal':
                        monthlyAmount = sub.cantidad * 4.33;
                        break;
                    case 'trimestral':
                        monthlyAmount = sub.cantidad / 3;
                        break;
                    case 'semestral':
                        monthlyAmount = sub.cantidad / 6;
                        break;
                    case 'anual':
                        monthlyAmount = sub.cantidad / 12;
                        break;
                }
                return total + monthlyAmount;
            }, 0);
    };

    const getTotalAnnualAmount = () => {
        return getTotalMonthlyAmount() * 12;
    };

    const getDaysUntilPayment = (date) => {
        const today = new Date();
        const paymentDate = new Date(date);
        const diffTime = paymentDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getUpcomingPayments = () => {
        return subscriptions
            .filter(sub => sub.activa)
            .filter(sub => {
                const daysUntil = getDaysUntilPayment(sub.fechaProximoPago);
                return daysUntil >= 0 && daysUntil <= sub.diasRecordatorio;
            })
            .length;
    };

    const chartData = categories.map(category => ({
        name: category.nombre,
        amount: subscriptions
            .filter(sub => sub.categoria === category.nombre && sub.activa)
            .reduce((sum, sub) => {
                let monthlyAmount = sub.cantidad;
                switch (sub.frecuenciaPago) {
                    case 'semanal':
                        monthlyAmount = sub.cantidad * 4.33;
                        break;
                    case 'trimestral':
                        monthlyAmount = sub.cantidad / 3;
                        break;
                    case 'semestral':
                        monthlyAmount = sub.cantidad / 6;
                        break;
                    case 'anual':
                        monthlyAmount = sub.cantidad / 12;
                        break;
                }
                return sum + monthlyAmount;
            }, 0)
    })).filter(data => data.amount > 0);

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-6 space-y-4 sm:space-y-0">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Mis Suscripciones</h1>
                                <p className="text-sm sm:text-base text-slate-600 font-medium">Gestione todas sus suscripciones y servicios recurrentes</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/subscription-categories')}
                                className="p-3 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Gestionar categorías"
                            >
                                <Settings className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => navigate('/subscriptions/new')}
                                className="bg-blue-900 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors flex items-center gap-2 shadow-lg"
                            >
                                <Plus className="w-5 h-5" />
                                Nueva Suscripción
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Total Mensual</p>
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">
                                    ${getTotalMonthlyAmount().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Total Anual</p>
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">
                                    ${getTotalAnnualAmount().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Suscripciones Activas</p>
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">
                                    {subscriptions.filter(sub => sub.activa).length}
                                </p>
                            </div>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Pagos Próximos</p>
                                <p className="text-xl sm:text-2xl font-bold text-slate-900">{getUpcomingPayments()}</p>
                            </div>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {chartData.length > 0 && (
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
                        <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">Gastos por Categoría</h3>
                        <div className="h-64 sm:h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        dataKey="amount"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        innerRadius={50}
                                        paddingAngle={5}
                                        animationBegin={0}
                                        animationDuration={800}
                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar suscripción..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                />
                            </div>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                            >
                                <option value="all">Todas las categorías</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.nombre}>{category.nombre}</option>
                                ))}
                            </select>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                            >
                                <option value="all">Todos los estados</option>
                                <option value="active">Activas</option>
                                <option value="inactive">Inactivas</option>
                            </select>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                            >
                                <option value="nextPayment">Próximo pago</option>
                                <option value="nombre">Nombre</option>
                                <option value="cantidad">Monto</option>
                                <option value="categoria">Categoría</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-3 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'} hover:bg-blue-200`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-3 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'} hover:bg-blue-200`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {loadingSubscriptions ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                        <p className="text-slate-600 font-medium">Cargando suscripciones...</p>
                    </div>
                ) : sortedSubscriptions.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                        <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {subscriptions.length === 0 ? 'No hay suscripciones' : 'No se encontraron resultados'}
                        </h3>
                        <p className="text-slate-600 mb-6">
                            {subscriptions.length === 0
                                ? 'Comience agregando su primera suscripción'
                                : 'Intente ajustar los filtros de búsqueda'}
                        </p>
                        {subscriptions.length === 0 && (
                            <button
                                onClick={() => navigate('/subscriptions/new')}
                                className="bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors flex items-center gap-2 mx-auto"
                            >
                                <Plus className="w-5 h-5" />
                                Agregar Suscripción
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={`transition-all ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6' : 'space-y-4'}`}>
                        {sortedSubscriptions.map((subscription) => {
                            const daysUntilPayment = getDaysUntilPayment(subscription.fechaProximoPago);
                            const isUpcoming = daysUntilPayment <= subscription.diasRecordatorio && daysUntilPayment >= 0;

                            return (
                                <div
                                    key={subscription.id}
                                    className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all ${isUpcoming && subscription.activa ? 'border-orange-200 bg-orange-50' : 'border-slate-200'} ${viewMode === 'list' ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4' : 'p-6'}`}
                                >
                                    <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">
                                                    {subscription.nombre}
                                                </h3>
                                                <div className="flex flex-wrap gap-2">
                                                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                                        {subscription.categoria}
                                                    </span>
                                                    {!subscription.activa && (
                                                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                                                            Inactiva
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => toggleSubscriptionStatus(subscription)}
                                                    disabled={loading}
                                                    className={`p-2 rounded-lg transition-colors ${subscription.activa ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                    title={subscription.activa ? 'Desactivar' : 'Activar'}
                                                >
                                                    {subscription.activa ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/subscriptions/edit/${subscription.id}`)}
                                                    className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(subscription.id, subscription.nombre)}
                                                    disabled={loading}
                                                    className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className={`space-y-3 ${viewMode === 'list' ? 'sm:flex sm:flex-wrap sm:gap-4 sm:space-y-0' : ''}`}>
                                            <div className="flex items-center justify-between sm:flex-1">
                                                <span className="text-slate-600 font-medium">Monto:</span>
                                                <span className="text-base sm:text-lg font-bold text-slate-900">
                                                    ${subscription.cantidad.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between sm:flex-1">
                                                <span className="text-slate-600 font-medium">Frecuencia:</span>
                                                <span className="text-slate-900 font-medium capitalize">
                                                    {subscription.frecuenciaPago}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between sm:flex-1">
                                                <span className="text-slate-600 font-medium">Próximo pago:</span>
                                                <span className={`font-medium ${isUpcoming && subscription.activa ? 'text-orange-600' : 'text-slate-900'}`}>
                                                    {new Date(subscription.fechaProximoPago).toLocaleDateString('es-MX')}
                                                </span>
                                            </div>
                                            {subscription.activa && isUpcoming && (
                                                <div className="flex items-center gap-2 p-3 bg-orange-100 rounded-lg sm:flex-1">
                                                    <Bell className="w-4 h-4 text-orange-600" />
                                                    <span className="text-sm font-medium text-orange-800">
                                                        Pago próximo en {daysUntilPayment} día{daysUntilPayment !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            )}
                                            {subscription.metodoPago && (
                                                <div className="flex items-center justify-between sm:flex-1">
                                                    <span className="text-slate-600 font-medium">Método:</span>
                                                    <span className="text-slate-900 font-medium">
                                                        {subscription.metodoPago}
                                                    </span>
                                                </div>
                                            )}
                                            {subscription.urlServicio && (
                                                <div className="flex items-center justify-between sm:flex-1">
                                                    <span className="text-slate-600 font-medium">Sitio web:</span>
                                                    <a
                                                        href={subscription.urlServicio}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                                    >
                                                        <Globe className="w-4 h-4" />
                                                    </a>
                                                </div>
                                            )}
                                            {subscription.notas && (
                                                <div className={`pt-3 border-t border-slate-200 ${viewMode === 'list' ? 'sm:flex-1' : ''}`}>
                                                    <p className="text-sm text-slate-600 font-medium">{subscription.notas}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {subscriptions.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-xl p-6 sm:p-8 mt-8 text-white">
                        <h3 className="text-xl sm:text-2xl font-bold mb-6">Resumen Financiero</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                            <div className="text-center">
                                <p className="text-blue-200 mb-2">Gasto Mensual</p>
                                <p className="text-xl sm:text-3xl font-bold">
                                    ${getTotalMonthlyAmount().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-blue-200 mb-2">Gasto Anual</p>
                                <p className="text-xl sm:text-3xl font-bold">
                                    ${getTotalAnnualAmount().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-blue-200 mb-2">Promedio por Suscripción</p>
                                <p className="text-xl sm:text-3xl font-bold">
                                    ${subscriptions.filter(s => s.activa).length > 0
                                        ? (getTotalMonthlyAmount() / subscriptions.filter(s => s.activa).length).toLocaleString('es-MX', { minimumFractionDigits: 2 })
                                        : '0.00'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {message.text && (
                    <div
                        className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-green-50 border border-green-200 text-green-800'}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {message.type === 'error' ? (
                                    <AlertCircle className="w-5 h-5 text-red-600" />
                                ) : (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                )}
                                <span className="font-semibold">{message.text}</span>
                            </div>
                            <button
                                onClick={() => setMessage({ text: '', type: '' })}
                                className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default XFinanceSubscriptionsList;